/**
 * Fall catch animation: a caught creature is sucked into the catcher flask. It shoots into the
 * mouth (speeding up, shrinking to fit the neck, spinning a little), drops into the potion with
 * a small bounce, bobs there, then sinks and fades out. Visual only: the catch itself (scoring,
 * stash, events) happens exactly as before; FallRenderer draws this ghost after the physics body
 * is gone, behind the flask glass so it reads as inside.
 *
 * Positions are run px. The flask points come from its 256×383 art frame (the skin is
 * letterboxed into the flask box), so they follow the flask as the player steers.
 */

export var SWALLOW_FLY_MS = 320;
export var SWALLOW_SETTLE_MS = 260;
export var SWALLOW_FADE_AT_MS = 1150;
export var SWALLOW_TOTAL_MS = 1650;

/** Share of the flight spent reaching the mouth (the rest drops it into the potion). */
var MOUTH_AT = 0.55;
/** Share of the drop (mouth → potion) still in the neck: it only grows to its inside size after. */
var NECK_CLEAR_AT = 0.4;

/**
 * Flask art frame and the points the animation aims at (frame px). `neck`: size squeezing
 * through the mouth (fits the neck); `size`: size once inside the body, in the potion.
 */
export var SWALLOW_FRAME = { w: 256, h: 383, mouthY: 6, neck: 66, size: 99 };
/**
 * Resting spots in the potion, used in turn so creatures caught close together don't stack.
 * Placed so a creature of `size` stays inside the glass through the landing dip and the sink.
 */
var REST = [[128, 294], [104, 298], [152, 296], [116, 300], [140, 299]];

function clamp01(t) {
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

function smooth(t) {
  var k = clamp01(t);
  return k * k * (3 - 2 * k);
}

/**
 * Where the flask's art frame sits: scale (run px per frame px) and its top-left corner.
 * @param {number} cx flask centre x (the player)
 * @param {{ flaskW: number, flaskH: number, flaskTopY: number }} dims
 */
export function swallowFrame(cx, dims) {
  var F = SWALLOW_FRAME;
  var s = Math.min(dims.flaskW / F.w, dims.flaskH / F.h);
  return {
    s: s,
    x0: cx - (F.w / 2) * s,
    y0: dims.flaskTopY + (dims.flaskH - F.h * s) / 2,
  };
}

/** The n-th resting spot in the potion, in run px for a frame. */
export function swallowRest(frame, n) {
  var r = REST[((n % REST.length) + REST.length) % REST.length];
  return { x: frame.x0 + r[0] * frame.s, y: frame.y0 + r[1] * frame.s };
}

/**
 * The creature's pose `age` ms after the catch.
 * @param {number} age
 * @param {{ x: number, y: number, size: number, angle: number }} start where it was caught
 * @param {{ x: number, y: number }} mouth the flask mouth now
 * @param {{ x: number, y: number }} rest its spot in the potion now
 * @param {number} size its size inside (px across)
 * @param {boolean} [reduced] reduced motion: no spin or bob
 * @param {number} [neckSize] its size squeezing through the mouth (default `size`); the dip,
 *   bob and sink are sized from it too, so a big inside size doesn't punch through the base
 * @returns {{ x: number, y: number, size: number, angle: number, alpha: number, inside: boolean }|null}
 *   null once it has faded out
 */
export function swallowPose(age, start, mouth, rest, size, reduced, neckSize) {
  if (!(age >= 0) || age >= SWALLOW_TOTAL_MS) return null;
  var neck = neckSize > 0 ? Math.min(neckSize, size) : size;
  var x, y, s, inside;
  var u = clamp01(age / SWALLOW_FLY_MS);
  if (u < MOUTH_AT) {
    // Sucked in: accelerating toward the mouth, shrinking to fit the neck.
    var a = u / MOUTH_AT;
    var k = a * a;
    x = start.x + (mouth.x - start.x) * k;
    y = start.y + (mouth.y - start.y) * k;
    s = start.size + (neck - start.size) * smooth(a);
    inside = false;
  } else {
    // Through the neck and down into the potion, slowing as it lands.
    var b = (u - MOUTH_AT) / (1 - MOUTH_AT);
    var e = 1 - (1 - b) * (1 - b) * (1 - b);
    x = mouth.x + (rest.x - mouth.x) * e;
    y = mouth.y + (rest.y - mouth.y) * e;
    // Out of the neck, it swells to its inside size as it drops into the potion.
    s = neck + (size - neck) * smooth((e - NECK_CLEAR_AT) / (1 - NECK_CLEAR_AT));
    inside = true;
  }
  var spin = reduced ? 0 : (start.x <= mouth.x ? 1.3 : -1.3) * smooth(u);
  var angle = start.angle + spin;
  if (age > SWALLOW_FLY_MS && !reduced) {
    var p = (age - SWALLOW_FLY_MS) / SWALLOW_SETTLE_MS;
    if (p < 1) {
      // Landing: a small dip into the potion and back.
      y += neck * 0.16 * Math.sin(p * Math.PI * 1.5) * (1 - p);
    } else {
      // Floating: a slow bob.
      y += neck * 0.035 * Math.sin((age - SWALLOW_FLY_MS - SWALLOW_SETTLE_MS) / 210);
    }
  }
  var alpha = 1;
  if (age > SWALLOW_FADE_AT_MS) {
    // Dissolving: sinks into the potion as it fades.
    var f = smooth((age - SWALLOW_FADE_AT_MS) / (SWALLOW_TOTAL_MS - SWALLOW_FADE_AT_MS));
    alpha = 1 - f;
    y += neck * 0.45 * f;
    s *= 1 - 0.25 * f;
  }
  return { x: x, y: y, size: s, angle: angle, alpha: alpha, inside: inside };
}
